import type { Locale } from "@chams-pay/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  prepareParentWhatsAppIntent,
  whatsappMessageFromIntent,
} from "./financial-document-share.js";

export interface PreparedWhatsAppIntent {
  readonly url: string | null;
  readonly preparing: boolean;
  readonly failed: boolean;
  readonly copied: boolean;
  readonly copyFailed: boolean;
  readonly prepare: () => void;
  readonly retry: () => void;
  readonly copy: () => Promise<boolean>;
}

export function usePreparedWhatsAppIntent({
  paymentId,
  locale,
  csrf,
  enabled,
  beforePrepare,
}: {
  readonly paymentId: string | null;
  readonly locale: Locale;
  readonly csrf: string;
  readonly enabled: boolean;
  readonly beforePrepare?: () => Promise<void>;
}): PreparedWhatsAppIntent {
  const [preparation, setPreparation] = useState<{
    readonly paymentId: string;
    readonly locale: Locale;
    readonly status: "preparing" | "ready" | "failed";
    readonly url?: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const serverReadyPayment = useRef<string | null>(null);
  const requestSequence = useRef(0);
  const inFlightKey = useRef<string | null>(null);
  const eligible = enabled && Boolean(paymentId) && Boolean(csrf);

  useEffect(() => {
    requestSequence.current += 1;
    inFlightKey.current = null;
    setPreparation(null);
    setCopied(false);
    setCopyFailed(false);

    return () => {
      requestSequence.current += 1;
      inFlightKey.current = null;
    };
  }, [csrf, enabled, locale, paymentId]);

  const current =
    preparation?.paymentId === paymentId && preparation.locale === locale ? preparation : null;
  const url = eligible && current?.status === "ready" && current.url ? current.url : null;
  const preparing = eligible && current?.status === "preparing";
  const failed = eligible && current?.status === "failed";

  const prepare = useCallback(() => {
    if (!eligible || !paymentId) return;
    const requestKey = `${paymentId}:${locale}:${csrf}`;
    if (inFlightKey.current === requestKey) return;
    inFlightKey.current = requestKey;
    const request = ++requestSequence.current;
    setCopied(false);
    setCopyFailed(false);
    setPreparation({ paymentId, locale, status: "preparing" });

    void (async () => {
      if (serverReadyPayment.current !== paymentId) {
        await beforePrepare?.();
        serverReadyPayment.current = paymentId;
      }
      return prepareParentWhatsAppIntent({ paymentId, locale, csrf });
    })()
      .then((intentUrl) => {
        if (requestSequence.current !== request) return;
        setPreparation({ paymentId, locale, status: "ready", url: intentUrl });
      })
      .catch(() => {
        if (requestSequence.current !== request) return;
        setPreparation({ paymentId, locale, status: "failed" });
      })
      .finally(() => {
        if (inFlightKey.current === requestKey) inFlightKey.current = null;
      });
  }, [beforePrepare, csrf, eligible, locale, paymentId]);

  const copy = useCallback(async () => {
    if (!url) return false;
    setCopied(false);
    setCopyFailed(false);
    try {
      await navigator.clipboard.writeText(whatsappMessageFromIntent(url));
      setCopied(true);
      return true;
    } catch {
      setCopyFailed(true);
      return false;
    }
  }, [url]);

  return { url, preparing, failed, copied, copyFailed, prepare, retry: prepare, copy };
}
